/**
 * Dynamic Tier Suggestion Utility
 * 
 * Calculates the minimum tier that satisfies ALL user needs.
 * This prevents suggesting a tier that doesn't actually work for the user.
 * 
 * Example: User has 55 vehicles and 0 team members
 * - Team tier (20 vehicles, 4 team) - WRONG: doesn't fit vehicles
 * - Growth tier (50 vehicles, 6 team) - WRONG: doesn't fit vehicles
 * - Enterprise tier (unlimited) - CORRECT: fits all needs
 */

import { TIER_PRICING } from '../config/pricing';
import { normalizePlanTier, TIER_DISPLAY_NAMES } from './planTier';

// Tier limits (must match backend TIER_LIMITS in capabilities.config.ts)
export const TIER_LIMITS = {
  free: {
    vehicles: 5,
    teamSize: 1,
    cohosts: 0,
  },
  trial: {
    // NOTE: These are SUGGESTION limits, not access limits.
    // Trial has FULL ACCESS (Infinity) for actual usage, but for tier
    // suggestions we use free-equivalent values so findSuggestedTier()
    // recommends paid tiers (solo/team/growth) instead of "trial".
    vehicles: 5,
    teamSize: 1,
    cohosts: 0,
  },
  solo: {
    vehicles: 15,
    teamSize: 1,
    cohosts: 0,
  },
  team: {
    vehicles: 40,
    teamSize: 3,
    cohosts: 10,
  },
  growth: {
    vehicles: 75,
    teamSize: 8,
    cohosts: 25,
  },
  enterprise: {
    vehicles: Infinity,
    teamSize: Infinity,
    cohosts: Infinity,
  },
  full_enterprise: {
    vehicles: Infinity,
    teamSize: Infinity,
    cohosts: Infinity,
  },
};

export const TIER_ORDER = ['free', 'trial', 'solo', 'team', 'growth', 'enterprise', 'full_enterprise'];

/**
 * Find the minimum tier that accommodates ALL user needs
 * 
 * @param {Object} usage - Current usage context
 * @param {number} usage.vehicles - Number of active vehicles (excluding sold/totaled)
 * @param {number} usage.teamMembers - Number of team members (including owner)
 * @param {number} usage.cohosts - Number of cohosts (optional)
 * @returns {string} The suggested tier name
 */
export function findSuggestedTier(usage) {
  const { vehicles = 0, teamMembers = 1, cohosts = 0 } = usage;

  for (const tier of TIER_ORDER) {
    const limits = TIER_LIMITS[tier];
    if (!limits) continue;

    // Check ALL dimensions - tier must satisfy ALL needs
    const vehiclesOk = limits.vehicles >= vehicles || limits.vehicles === Infinity;
    const teamOk = limits.teamSize >= teamMembers || limits.teamSize === Infinity;
    const cohostsOk = !cohosts || limits.cohosts >= cohosts || limits.cohosts === Infinity;

    // If this tier satisfies ALL requirements, return it
    if (vehiclesOk && teamOk && cohostsOk) {
      return tier;
    }
  }

  return 'enterprise'; // Enterprise is unlimited, always works
}

/**
 * Get a human-readable explanation of why a specific tier is suggested
 * 
 * @param {Object} usage - Current usage context
 * @param {string} currentTier - User's current tier
 * @returns {Object} Suggestion details
 */
export function getTierSuggestionDetails(usage, currentTier) {
  const normalizedCurrentTier = normalizePlanTier(currentTier);
  const suggestedTier = findSuggestedTier(usage);
  const currentLimits = TIER_LIMITS[normalizedCurrentTier] || TIER_LIMITS.free;
  const suggestedLimits = TIER_LIMITS[suggestedTier];
  const suggestedPricing = TIER_PRICING[suggestedTier];

  // Determine which limits are exceeded
  const exceededLimits = [];
  
  if (usage.vehicles > currentLimits.vehicles && currentLimits.vehicles !== Infinity) {
    exceededLimits.push({
      type: 'vehicles',
      current: usage.vehicles,
      limit: currentLimits.vehicles,
      suggested: suggestedLimits.vehicles === Infinity ? 'Unlimited' : suggestedLimits.vehicles,
    });
  }
  
  if (usage.teamMembers > currentLimits.teamSize && currentLimits.teamSize !== Infinity) {
    exceededLimits.push({
      type: 'teamMembers',
      current: usage.teamMembers,
      limit: currentLimits.teamSize,
      suggested: suggestedLimits.teamSize === Infinity ? 'Unlimited' : suggestedLimits.teamSize,
    });
  }

  if (usage.cohosts && usage.cohosts > currentLimits.cohosts && currentLimits.cohosts !== Infinity) {
    exceededLimits.push({
      type: 'cohosts',
      current: usage.cohosts,
      limit: currentLimits.cohosts,
      suggested: suggestedLimits.cohosts === Infinity ? 'Unlimited' : suggestedLimits.cohosts,
    });
  }

  // Build human-readable message
  let message = '';
  if (exceededLimits.length === 1) {
    const limit = exceededLimits[0];
    const typeLabel = {
      vehicles: 'vehicles',
      teamMembers: 'team members',
      cohosts: 'co-hosts',
    }[limit.type];
    message = `You have ${limit.current} ${typeLabel} but your ${TIER_DISPLAY_NAMES[normalizedCurrentTier] || normalizedCurrentTier} plan only allows ${limit.limit}.`;
  } else if (exceededLimits.length > 1) {
    const parts = exceededLimits.map(limit => {
      const typeLabel = {
        vehicles: 'vehicles',
        teamMembers: 'team members',
        cohosts: 'co-hosts',
      }[limit.type];
      return `${limit.current} ${typeLabel} (limit: ${limit.limit})`;
    });
    message = `You have ${parts.join(' and ')} on your ${TIER_DISPLAY_NAMES[normalizedCurrentTier] || normalizedCurrentTier} plan.`;
  }

  return {
    currentTier: normalizedCurrentTier,
    suggestedTier,
    exceededLimits,
    message,
    suggestedLimits: {
      vehicles: suggestedLimits.vehicles === Infinity ? 'Unlimited' : suggestedLimits.vehicles,
      teamSize: suggestedLimits.teamSize === Infinity ? 'Unlimited' : suggestedLimits.teamSize,
      cohosts: suggestedLimits.cohosts === Infinity ? 'Unlimited' : suggestedLimits.cohosts,
    },
    pricing: suggestedPricing,
    isUpgradeNeeded:
      TIER_ORDER.indexOf(suggestedTier) >
      TIER_ORDER.indexOf(normalizedCurrentTier),
  };
}

/**
 * Check if a tier can accommodate specific needs
 * 
 * @param {string} tier - Tier to check
 * @param {Object} needs - Required limits
 * @returns {boolean} Whether the tier can accommodate the needs
 */
export function tierCanAccommodate(tier, needs) {
  const limits = TIER_LIMITS[normalizePlanTier(tier)];
  if (!limits) return false;

  const vehiclesOk = !needs.vehicles || limits.vehicles >= needs.vehicles || limits.vehicles === Infinity;
  const teamOk = !needs.teamMembers || limits.teamSize >= needs.teamMembers || limits.teamSize === Infinity;
  const cohostsOk = !needs.cohosts || limits.cohosts >= needs.cohosts || limits.cohosts === Infinity;

  return vehiclesOk && teamOk && cohostsOk;
}

/**
 * Get the next tier up from the current tier
 * Note: This is a SIMPLE upgrade, not always the best choice.
 * Use findSuggestedTier() for smart suggestions.
 * 
 * @param {string} currentTier - Current tier
 * @returns {string} Next tier up
 */
export function getNextTierUp(currentTier) {
  const currentIndex = TIER_ORDER.indexOf(normalizePlanTier(currentTier));
  if (currentIndex === -1 || currentIndex >= TIER_ORDER.length - 1) {
    return TIER_ORDER[TIER_ORDER.length - 1];
  }
  return TIER_ORDER[currentIndex + 1];
}

export default {
  TIER_LIMITS,
  TIER_ORDER,
  findSuggestedTier,
  getTierSuggestionDetails,
  tierCanAccommodate,
  getNextTierUp,
};
